import { ReactNode } from 'react'
import { Colors } from '../../util/types'
import { PerSpacerProps } from '../Spacer'
import { marginClasses } from '../../util/less-util'
import { icons } from '../Icon/icons'
import PerIcon from '../Icon'
import { Tooltip } from 'antd'
import './index.less'
import { useTranslation } from 'react-i18next'
import PerText from '../Text'

export type isProcessingCircleButton = {
  active: boolean
  tooltip?: {
    heading?: string
    text?: string | false
  }
}

export interface PerButtonCircleProps extends PerSpacerProps, React.ButtonHTMLAttributes<HTMLButtonElement> {
  color?: Colors
  variant?: 'pastel' | 'bold' | 'default' | 'transparent-variant' | 'gray-variant'
  size?: 'xsmall' | 'small' | 'large'
  disabled?: boolean
  icon?: keyof typeof icons
  iconVariant?: 'decoration' | 'clickable'
  number?: number
  tooltip?: string | ReactNode
  asLink?: string
  isProcessing?: isProcessingCircleButton
  customIconComponent?: JSX.Element
}

const PerButtonCircle: React.FC<PerButtonCircleProps> = ({
  color,
  variant = 'default',
  size,
  number,
  icon,
  iconVariant = 'clickable',
  disabled,
  tooltip,
  asLink,
  isProcessing,
  customIconComponent,
  m,
  mx,
  my,
  mt,
  mr,
  mb,
  ml,
  className,
  ...props
}) => {
  const [t] = useTranslation()
  const btn = 'per-btnCircle'
  const modifiers = marginClasses(m, mb, mt, ml, mr, mx, my, 'per-spacer')
  const processingIsActive = isProcessing && isProcessing.active

  if (color) {
    modifiers.push(`${btn}--${color}`)
  }
  if (variant !== undefined) {
    modifiers.push(`${btn}--${variant}`)
  }
  if (size) {
    modifiers.push(`${btn}--${size}`)
  }
  if (className) {
    modifiers.push(className)
  }
  if (number !== undefined && number > 9) {
    modifiers.push(`smallNumber`)
  }
  if (disabled || processingIsActive) {
    modifiers.push(`${btn}--disabled`)
  }

  const iconSize = () => {
    if (size === 'xsmall') {
      return 8 // @btn-xsmall_icon-size
    } else if (size === 'small') {
      return 12 // @btn-small_icon-size
    } else if (size === 'large') {
      return 20 // @btn-large_icon-size
    } else {
      return 16 // @btn_icon-size
    }
  }

  const renderTooltip = () => {
    if (isProcessing && isProcessing.active) {
      const text = isProcessing.tooltip && isProcessing.tooltip.text

      const renderText = () => {
        if (text === false) {
          return undefined
        } else if (text) {
          return text
        } else {
          return ''
        }
      }

      return (
        <>
          <PerText fontWeight="bold" mb={text === false ? undefined : 'xxxs'}>
            {isProcessing.tooltip && isProcessing.tooltip.heading
              ? isProcessing.tooltip.heading
              : t('global.button.isProcessing.processing')}
          </PerText>
          {renderText()}
        </>
      )
    } else if (tooltip) {
      return tooltip
    } else {
      return undefined
    }
  }
  const renderIcon = () => {
    if (isProcessing && isProcessing.active) {
      return <PerIcon name="loading_fill" size={iconSize()} spin />
    } else if (customIconComponent) {
      return customIconComponent
    } else if (icon) {
      return <PerIcon name={icon} size={iconSize()} variant={iconVariant} />
    } else {
      return undefined
    }
  }

  const Inner = () => {
    return (
      <Tooltip title={renderTooltip()}>
        <span>
          {renderIcon()}
          {number}
        </span>
      </Tooltip>
    )
  }

  return (
    <>
      {asLink ? (
        <a className={'per-btnCircle ' + modifiers.join(' ')} href={asLink} download="">
          <Inner />
        </a>
      ) : (
        <button
          className={'per-btnCircle ' + modifiers.join(' ')}
          disabled={disabled || processingIsActive}
          type="button"
          {...props}
          data-testid={props.testid}
        >
          <Inner />
        </button>
      )}
    </>
  )
}

export default PerButtonCircle
